// Import Core
import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
// Import Libraries
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
// Import Utils
import getNavItemLinks, { GetListLinks } from '../../utils/getNavItemLinks';
import { getSlug } from '../../utils/getSlug';
import { renderFontAwesomeIcon } from '../../utils/renderFontAwesomeIcon';

/* Styled Components */
const FooterStyle = styled.footer`
	background: var(--primary-500);
	color: var(--white);
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 64px 120px;
	gap: 40px;
	/* margin-bottom: 80px; */
	@media (max-width: 992px) {
		/* padding: 20px 64px 64px; */
		gap: 32px;
	}
	@media (max-width: 768px) {
		padding: 32px 24px;
	}
	.top {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-start;
		width: 100%;
		gap: 40px;
		@media (max-width: 992px) {
			flex-direction: column;
		}

		.footer-menu-list {
			display: flex;
			flex-wrap: wrap;
			gap: 40px;
			ul {
				margin: 16px 0 32px;
				/* min-width: 165px; */
				list-style-type: none;
				li {
					margin-bottom: 8px;
					&:last-child {
						margin-bottom: unset;
					}
					a {
						color: var(--white);
					}
				}
			}
		}
		.sponsor-list {
			/* @media (max-width: 768px) {
				margin-top: 32px;
			} */
			h3 {
				margin-bottom: 16px;
			}
			ul {
				min-width: 376px;
				display: flex;
				justify-content: space-between;
				list-style-type: none;
				flex-wrap: wrap;
				align-items: center;
				gap: 10px;
				@media (max-width: 768px) {
					display: flex;
					justify-content: flex-start;
					gap: 20px;
					flex-flow: wrap;
					min-width: unset;
				}
				* {
					color: var(--white);
				}
				li {
					width: 100px;
					text-align: center;
					@media (max-width: 768px) {
						width: 89px;
					}
					/* .gatsby-image-wrapper {
						height: 100px;
						img {
							object-fit: contain !important;
							background: white;
						}
					} */
					.sponsor-name {
						margin-top: 8px;
					}
				}
			}
		}
	}
	.bottom {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-start;
		width: 100%;
		@media (max-width: 768px) {
			flex-direction: column;
			justify-content: center;
			align-items: center;
		}
		.left {
			@media (max-width: 768px) {
				width: 100%;
				text-align: center;
				display: flex;
				flex-direction: column;
				align-items: center;
			}
			/* .logo {
				height: 56px;
				width: 56px;
			} */

			.logo {
			width: 270px;
			/* width: 100%; */
			height: 64px; 
			@media (max-width: 768px) {
				
			}
		}
		}
		.right {
			.social-icons {
				display: flex;
				gap: 8px;
				justify-content: flex-end;
				align-items: center;
				margin-bottom: 16px;
				@media (max-width: 768px) {
					justify-content: center;
					margin: 24px 0;
				}
				.social-icon {
					width: 32px;
					height: 32px;
					color: var(--white);
					background: var(--primary-500);
					border: 1px solid var(--neutral-300);
					border-radius: 50%;
					display: flex;
					justify-content: center;
					align-items: center;
				}
			}
			.policy-links {
				display: flex;
				margin-right: 32px;
				list-style-type: none;
				li {
					display: flex;
					&:after {
						content: '/';
						padding: 0 8px;
					}
					&:last-child {
						&:after {
							content: '';
							padding: 0;
						}
					}
					a {
						color: var(--white);
					}
				}
			}
		}
	}
`;

/* Components */
const MenuList = ({ menus }) => {
	return (
		<div className='footer-menu-list'>
			{menus.map((menu, index) => {
				return (
					<div className='menu' key={index}>
						<h5 className='menu-title'>{menu.title}</h5>
						<ul>
							{menu.items.map((item, index) => {
								return <GetListLinks item={item} index={index} key={index} />;
							})}
						</ul>
					</div>
				);
			})}
		</div>
	);
};

const SponsorList = ({ sponsors }) => {
	// Same content for each list item
	const ListContent = ({ item }) => {
		return (
			<>
				<GatsbyImage image={getImage(item.image.asset)} alt={item.navItem.name} />
				<p className='small sponsor-name'>{item.navItem.name}</p>
			</>
		);
	};

	// loop through sponsors and get link if there is one
	const getSponsors = (item, index) => {
		// Each sponsor item has option for internal or external link
		// Then check if correspondong link has content
		const { displayExternalLink, link } = getNavItemLinks(item.navItem);

		// Three options:
		// 1. Internal Link
		if (displayExternalLink === false && link !== null) {
			const slug = getSlug(link)
			return (
				<li key={index}>
					<Link to={slug}>
						<ListContent item={item} />
					</Link>
				</li>
			);
		}
		// 2. External Link
		else if (displayExternalLink === true && link !== null) {
			return (
				<li key={index}>
					<a href={link}>
						<ListContent item={item} />
					</a>
				</li>
			);
		}
		// 3. No Link TODO: IS THIS NEEDED?
		else {
			return (
				<li key={index}>
					<ListContent item={item} />
				</li>
			);
		}
	};

	return (
		<div className='sponsor-list'>
			<h3 className='small'>Sponsors</h3>
			<ul>{sponsors.map(getSponsors)}</ul>
		</div>
	);
};

const SocialIcons = ({ socialLinks }) => {
	return (
		<div className='social-icons'>
			{socialLinks.map((link) => {
				const Icon = renderFontAwesomeIcon(link.socialIcon.name);
				return (
					<a
						key={link._key}
						href={
							link.navItem.navigationItemUrl.displayExternal
								? link.navItem.navigationItemUrl.externalUrl
								: link.navItem.navigationItemUrl.internalUrl
						}
						target='_blank'
						className='social-icon small'
						aria-label={link.socialIcon.name}
					>
						{Icon}
					</a>
				);
			})}
		</div>
	);
};

const PolicyLinks = ({ policyLinks }) => {
	return (
		<ul className='policy-links'>
			{policyLinks.map((item, index) => {
				return <GetListLinks item={item} index={index} key={index} />;
			})}
		</ul>
	);
};

/* Render Component */
const Footer = ({ footer, socialLinks, logo }) => {
	return (
		<FooterStyle>
			<div className='top'>
				{footer.footerMenus.length > 0 && (
					<MenuList menus={footer.footerMenus} />
				)}

				{footer.sponsors.length > 0 && (
					<SponsorList sponsors={footer.sponsors} />
				)}
			</div>

			<hr />

			<div className='bottom'>
				<div className='left'>
					<Link to='/'>
						<GatsbyImage image={getImage(logo)} alt='Good Hotel Guide' className='logo' />
					</Link>
					<p className='small'>
						&copy; {new Date().getFullYear()} Good Hotel Guide. All rights
						reserved.
					</p>
				</div>
				<div className='right'>
					{socialLinks.length > 0 && <SocialIcons socialLinks={socialLinks} />}

					{footer.policyLinks && footer.policyLinks.items.length > 0 && (
						<PolicyLinks policyLinks={footer.policyLinks.items} />
					)}
				</div>
			</div>
		</FooterStyle>
	);
};
export default Footer;
